import { ComponentProps, useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { FormattedMessage, useIntl } from 'react-intl';
import type { DateRangeValue } from './date-range-presets';
import { DateRangePickerLayout } from './DateRangePickerLayout';
import { getDayPickerLocale } from './day-picker-locale';
import { useSharedDayPickerStyles } from './useSharedDayPickerStyles';

export interface CustomFixedRangePickerProps {
  open: boolean;
  onClose: () => void;
  onApply: (range: DateRangeValue) => void;
  /** Initial range for the picker. */
  initialRange?: DateRangeValue | null;
  /**
   * Earliest selectable instant; DayPicker disables days strictly before this boundary (absolute `Date`, not re-timezoned).
   */
  minSelectableDate?: Date;
  /**
   * Latest selectable instant; DayPicker disables days strictly after this boundary (absolute `Date`).
   */
  maxSelectableDate?: Date;
}

function normalizeRange(from: Date, to: Date): DateRangeValue {
  return from.getTime() <= to.getTime() ? { from, to } : { from: to, to: from };
}

/**
 * Inline range date picker for "Custom fixed date range".
 * Renders below the selector (inside Menu/Popover). Two months, no time selection.
 */
export function CustomFixedRangePicker({
  open,
  onClose,
  onApply,
  initialRange,
  minSelectableDate,
  maxSelectableDate,
}: CustomFixedRangePickerProps) {
  const { locale } = useIntl();
  const dayPickerStyles = useSharedDayPickerStyles();
  const [selected, setSelected] = useState<DateRange | undefined>(() => {
    if (initialRange?.from && initialRange?.to) {
      return {
        from: new Date(initialRange.from),
        to: new Date(initialRange.to),
      };
    }
    return undefined;
  });

  // Sync selection when range view opens; intentionally not when initialRange changes so we don't overwrite user selection.
  useEffect(() => {
    if (!open) return;
    if (initialRange?.from && initialRange?.to) {
      setSelected({
        from: new Date(initialRange.from),
        to: new Date(initialRange.to),
      });
    } else {
      setSelected(undefined);
    }
  }, [open]);

  const disabled = useMemo((): ComponentProps<typeof DayPicker>['disabled'] => {
    const matchers: Array<{ before: Date } | { after: Date }> = [];
    if (minSelectableDate) {
      matchers.push({ before: minSelectableDate });
    }
    if (maxSelectableDate) {
      matchers.push({ after: maxSelectableDate });
    }
    if (matchers.length === 0) {
      return undefined;
    }
    return matchers.length === 1 ? matchers[0] : matchers;
  }, [minSelectableDate, maxSelectableDate]);

  const handleApply = () => {
    if (!selected?.from) return;
    const to = selected.to ?? selected.from;
    const fromDate = new Date(selected.from);
    fromDate.setHours(0, 0, 0, 0);
    const toDate = new Date(to);
    toDate.setHours(23, 59, 59, 999);
    onApply(normalizeRange(fromDate, toDate));
    onClose();
  };

  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  };
  const fromLabel = selected?.from ? selected.from.toLocaleDateString(locale, dateOptions) : '—';
  const toLabel = selected?.to ? selected.to.toLocaleDateString(locale, dateOptions) : '—';

  return (
    <DateRangePickerLayout
      title={
        <FormattedMessage
          id="common.dateRangeSelector.selectDateRangeTitle"
          defaultMessage="Seleccionar rango de fechas"
        />
      }
      summary={
        <FormattedMessage
          id="common.dateRangeSelector.selectedLabel"
          defaultMessage="Seleccionado: {from} – {to}"
          values={{ from: fromLabel, to: toLabel }}
        />
      }
      onCancel={onClose}
      onApply={handleApply}
      applyDisabled={!selected?.from}
    >
      <DayPicker
        mode="range"
        selected={selected}
        onSelect={setSelected}
        numberOfMonths={2}
        defaultMonth={selected?.from ?? new Date()}
        locale={getDayPickerLocale(locale)}
        styles={dayPickerStyles}
        disabled={disabled}
      />
    </DateRangePickerLayout>
  );
}
